import React from "react";
import {
  Box,
  Flex,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
} from "@chakra-ui/react";

export interface PopoverMenuItem {
  id?: string | number;
  startIcon?: React.ReactNode;
  text: string;
  endIcon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface PopoverDropdownProps {
  items: PopoverMenuItem[];
  children: React.ReactNode;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  placement?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-start"
    | "top-end"
    | "bottom-start"
    | "bottom-end"
    | "left-start"
    | "left-end"
    | "right-start"
    | "right-end";
  width?: string | number;
  minWidth?: string | number;
  maxWidth?: string | number;
  closeOnBlur?: boolean;
  closeOnSelect?: boolean;
}

export const PopoverDropdown: React.FC<PopoverDropdownProps> = ({
  items,
  children,
  isOpen,
  onOpen,
  onClose,
  placement = "bottom-start",
  width = "auto",
  minWidth = "160px",
  maxWidth = "300px",
  closeOnBlur = true,
  closeOnSelect = true,
}) => {
  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const isControlled = isOpen !== undefined;

  const handleOpen = () => {
    if (!isControlled) {
      setInternalIsOpen(true);
    }
    onOpen?.();
  };

  const handleClose = () => {
    if (!isControlled) {
      setInternalIsOpen(false);
    }
    onClose?.();
  };

  const handleItemClick = (item: PopoverMenuItem, e?: React.MouseEvent) => {
    if (item.disabled) return;
    if (e) {
      e.stopPropagation();
    }
    item.onClick();
    if (closeOnSelect && !isControlled) {
      setInternalIsOpen(false);
    }
    if (closeOnSelect) {
      onClose?.();
    }
  };

  const currentIsOpen = isControlled ? isOpen : internalIsOpen;

  return (
    <Popover
      isOpen={currentIsOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      placement={placement}
      closeOnBlur={closeOnBlur}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          width={width}
          minWidth={minWidth}
          maxWidth={maxWidth}
          bg="white"
          border="1px solid #E5E7EB"
          borderRadius="8px"
          boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
          p="4px"
          onClick={(e) => e.stopPropagation()}
          _focus={{
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <PopoverBody p="0">
            <Flex flexDirection="column" gap="0">
              {items?.map((item, index) => (
                <Flex
                  key={item?.id || index}
                  alignItems="center"
                  px="12px"
                  py="8px"
                  cursor={item?.disabled ? "not-allowed" : "pointer"}
                  onClick={(e) => handleItemClick(item, e)}
                  opacity={item?.disabled ? 0.5 : 1}
                  _hover={
                    item?.disabled
                      ? {}
                      : {
                          bg: "#F3F4F6",
                        }
                  }
                  transition="background-color 0.2s"
                  borderRadius="6px"
                  mx="4px"
                  my="2px"
                >
                  {item?.startIcon && (
                    <Box
                      mr="12px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="#374151"
                    >
                      {item?.startIcon}
                    </Box>
                  )}
                  <Text
                    flex="1"
                    fontSize="14px"
                    fontWeight="400"
                    color="#111827"
                    fontFamily="sans-serif"
                  >
                    {item?.text}
                  </Text>
                  {item?.endIcon && (
                    <Box
                      ml="12px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="#374151"
                    >
                      {item?.endIcon}
                    </Box>
                  )}
                </Flex>
              ))}
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
